import React, { useState } from 'react';
import { Check, User, Mail, Send, AlertCircle, X, Loader2 } from 'lucide-react';

const UserCreationForm = () => {
  // API base URL - adjust this based on your environment
  const API_BASE_URL = 'http://localhost:8000/api/encrypted/users/create';
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    dataPolicyAccepted: false,
    newsletterSubscribed: false,
    hasAdminCreated: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isUserCreated, setIsUserCreated] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.dataPolicyAccepted) {
      newErrors.dataPolicyAccepted = 'You must accept the website data policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to extract error message from FastAPI validation errors
  const extractErrorMessage = (error) => {
    // If error is a string, return it directly
    if (typeof error === 'string') {
      return error;
    }
    
    // If it's an array of validation errors (FastAPI format)
    if (Array.isArray(error)) {
      // Extract messages from validation error objects
      return error.map(err => {
        if (typeof err === 'string') return err;
        if (err.msg) return err.msg;
        if (err.message) return err.message;
        return 'Validation error';
      }).join(', ');
    }
    
    // If it's an object with a detail property (FastAPI error format)
    if (error.detail) {
      // detail could be a string or array
      if (typeof error.detail === 'string') {
        return error.detail;
      }
      if (Array.isArray(error.detail)) {
        return error.detail.map(err => {
          if (typeof err === 'string') return err;
          if (err.msg) return err.msg;
          if (err.message) return err.message;
          return 'Validation error';
        }).join(', ');
      }
    }
    
    // If it's an object with msg property
    if (error.msg) return error.msg;
    if (error.message) return error.message;
    
    // Fallback
    return 'An error occurred. Please try again.';
  };

  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
// Make API call to create user
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          dataPolicyAccepted: formData.dataPolicyAccepted,
          newsletterSubscribed: formData.newsletterSubscribed,
          hasAdminCreated: formData.hasAdminCreated
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Success
        setUserId(data.id);
        setIsUserCreated(true);
        console.log('User created successfully:', data);
      } else {
        // API returned an error
        const errorMessage = extractErrorMessage(data);
        setErrors({ general: errorMessage });
        console.error('API error:', data);
      }
    } catch (error) {
      console.error('Network error:', error);
      setErrors({ 
        general: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!userId) {
      setErrors({ general: 'No user ID available. Please create user first.' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Get the current user ID from your auth system if available
      // For now, we'll pass null or you can get it from localStorage/context
      const currentUserId = localStorage.getItem('currentUserId') || null;

      // Make API call to send invitation
      const response = await fetch(`${API_BASE_URL}/${userId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          invitedBy: currentUserId // This could be null if not logged in
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Success
        setInvitationSent(true);
        console.log('Invitation sent successfully:', data);
      } else {
        // API returned an error
        const errorMessage = extractErrorMessage(data);
        setErrors({ general: errorMessage });
        console.error('API error:', data);
      }
    } catch (error) {
      console.error('Network error:', error);
      setErrors({ 
        general: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      dataPolicyAccepted: false,
      newsletterSubscribed: false
    });
    setErrors({});
    setUserId(null);
    setIsUserCreated(false);
    setInvitationSent(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full max-w-2xl">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New User</h2>
          <p className="text-gray-600">Fill in the details to create a new user account</p>
        </div>

        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <span className="text-red-700">{String(errors.general)}</span>
          </div>
        )}

        {isUserCreated && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800">User created successfully!</p>
                <p className="text-sm mt-1 text-green-700">User ID: {userId}</p>
              </div>
            </div>
          </div>
        )}

        {invitationSent && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Send className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-800">Invitation sent successfully!</p>
                <p className="text-sm mt-1 text-blue-700">An invitation has been sent to {formData.email}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isUserCreated || isSubmitting}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="user@example.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{String(errors.email)}</p>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="firstName">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isUserCreated || isSubmitting}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                  errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{String(errors.firstName)}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="lastName">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isUserCreated || isSubmitting}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                  errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{String(errors.lastName)}</p>
              )}
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className={`p-4 bg-gray-50 rounded-lg border ${
              errors.dataPolicyAccepted ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}>
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="dataPolicyAccepted"
                  checked={formData.dataPolicyAccepted}
                  onChange={handleInputChange}
                  disabled={isUserCreated || isSubmitting}
                  className="w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed mt-0.5"
                />
                <span className="ml-3 text-sm text-gray-700">
                  I accept the website data policy *
                </span>
              </label>
              {errors.dataPolicyAccepted && (
                <p className="mt-2 text-sm text-red-600 ml-8">{String(errors.dataPolicyAccepted)}</p>
              )}
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="newsletterSubscribed"
                  checked={formData.newsletterSubscribed}
                  onChange={handleInputChange}
                  disabled={isUserCreated || isSubmitting}
                  className="w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed mt-0.5"
                />
                <span className="ml-3 text-sm text-gray-700">
                  Subscribe to newsletter
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCreateUser}
              disabled={isSubmitting || isUserCreated}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting && !isUserCreated ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <User className="w-4 h-4" />
                  Create User
                </>
              )}
            </button>

            <button
              onClick={handleSendInvitation}
              disabled={!isUserCreated || isSubmitting || invitationSent}
              className="flex-1 px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting && isUserCreated ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </button>
          </div>

          {/* Reset Button */}
          {(isUserCreated || invitationSent) && (
            <button
              onClick={handleReset}
              className="w-full px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Create Another User
            </button>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-500 text-sm">* Required fields</p>
          {process.env.NODE_ENV === 'development' && userId && (
            <p className="text-gray-400 text-xs mt-2">Debug: User ID = {userId}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCreationForm;